/**
 * Formats a duration in seconds into a human-readable string.
 *
 * - `type = '1'` (default): prime notation — `2'30''` for minutes/seconds, `1'2''3'''` for h/m/s.
 *   Returns `'-'` for 0 seconds. Throws on negative input.
 * - `type = '2'`: zero-padded colon notation — `HH:MM:SS`.
 *
 * @param totalSeconds - Non-negative integer number of seconds.
 * @param type - Output format: `'1'` for prime notation, `'2'` for `HH:MM:SS`.
 * @returns Formatted duration string.
 * @throws {Error} If `type = '1'` and `totalSeconds` is negative.
 *
 * @example
 * formatSecondsToHMS(90)        // "1'30''"
 * formatSecondsToHMS(90, '2')   // "00:01:30"
 * formatSecondsToHMS(0)         // "-"
 */
export function formatSecondsToHMS(totalSeconds: number, type: '1' | '2' = '1'): string {
  if (type === '2') {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':')
  }

  // Handle negative input
  if (totalSeconds < 0) {
    throw new Error('Input cannot be negative')
  }

  if (totalSeconds === 0) {
    return '-'
  }

  // Calculate hours, minutes, and seconds
  const hours = Math.floor(totalSeconds / 3600)
  const remainingSecondsAfterHours = totalSeconds % 3600
  const minutes = Math.floor(remainingSecondsAfterHours / 60)
  const seconds = remainingSecondsAfterHours % 60

  // Build the formatted string
  const parts: string[] = []

  if (hours > 0) {
    parts.push(`${hours}'`)
  }

  if (minutes > 0) {
    parts.push(`${minutes}''`)
  }

  if (seconds > 0 || (hours === 0 && minutes === 0)) {
    // Always show seconds if there are no hours and minutes
    parts.push(`${seconds}'''`)
  }

  // Join the parts with appropriate connectors
  if (parts.length === 1) {
    const firstPart = parts[0]

    if (!firstPart) throw new Error('Invalid first part in format-seconds-to-hms')

    return firstPart
  } else if (parts.length === 2) {
    const firstPart = parts[0]
    const secondPart = parts[1]

    if (!firstPart || !secondPart) throw new Error('Invalid first or second part in format-seconds-to-hms')

    return `${parts[0]}${parts[1]}`
  } else if (parts.length === 3) {
    const firstPart = parts[0]
    const secondPart = parts[1]
    const thirdPart = parts[2]

    if (!firstPart || !secondPart || !thirdPart)
      throw new Error('Invalid first, second, or third part in format-seconds-to-hms')

    return `${parts[0]}${parts[1]}${parts[2]}`
  } else {
    return '-'
  }
}
